import type { ContentHash, ContentRef } from "@cogo/publisher-core";

import type {
  ConsumerValueSchema,
  HostChannelId,
  ParameterChannelId,
  ProjectedResultChannel,
  ResultChannelId,
} from "./action-consumer-catalog";
import type {
  AcceptanceCapabilityRequirement,
  AcceptanceInventoryKind,
} from "./application-acceptance-manifest";
import type { HostValueSchema } from "./host-capability-manifest";
import {
  compareCanonicalText,
  hashCanonicalContent,
  stableCanonicalStringify,
} from "./canonical-content-hash";

export type { HostChannelId, ParameterChannelId, ResultChannelId };

type RuntimeProjectedCondition = NonNullable<ProjectedResultChannel["when"]>;

export interface ProjectedCapabilityCondition
  extends Omit<RuntimeProjectedCondition, "param"> {
  parameterPath: RuntimeProjectedCondition["param"];
}

export interface RequiredCapabilitySourceInventoryRow {
  kind: AcceptanceInventoryKind;
  id: string;
}

export interface RequiredParameterChannel {
  channelId: ParameterChannelId;
  requestedAction: string;
  canonicalAction: string;
  parameterPath: string;
  aliases: readonly string[];
  role: "input" | "selector" | "payload" | "target" | "guard";
  condition?: ProjectedCapabilityCondition;
  sourceRefs: readonly ContentRef[];
  runtimeRequired: boolean;
  mandatory: boolean;
}

export interface RequiredResultChannel {
  channelId: ResultChannelId;
  requestedAction: string;
  canonicalAction: string;
  kind: "state-target" | "return-capture" | "host-effect" | "host-registry";
  target?: string;
  hostCapabilityId?: `host.${string}`;
  operation?: string;
  targetParam?: string;
  defaultTarget?: string;
  defaultTargetFromParam?: { prefix: string; param: string; fallback?: string };
  targetSuffixes: readonly string[];
  capture?: "step-name";
  selectorParam?: string;
  payloadParam?: string;
  envelope?: ConsumerValueSchema;
  condition?: ProjectedCapabilityCondition;
  sourceRefs: readonly ContentRef[];
  mandatory: boolean;
}

export interface RequiredActionConsumer {
  requestedAction: string;
  canonicalAction: string;
  aliases: readonly string[];
  requiredHostCapabilityIds: readonly `host.${string}`[];
  missingConsumerPolicy: "reject-before-side-effects" | "block-host";
  sourceRefs: readonly ContentRef[];
  mandatory: boolean;
}

export interface RequiredHostCapability {
  channelId: HostChannelId;
  requestedAction: string;
  canonicalAction: string;
  capabilityId: `host.${string}`;
  operation: string;
  condition?: ProjectedCapabilityCondition;
  sourceRefs: readonly ContentRef[];
  mandatory: boolean;
}

export interface RequiredCapabilityDependency {
  catalogId: AcceptanceCapabilityRequirement["catalogId"];
  kind: AcceptanceCapabilityRequirement["kind"];
  sourceInventoryRows: readonly RequiredCapabilitySourceInventoryRow[];
  sourceRefs: readonly ContentRef[];
  mandatory: true;
}

export interface RequiredHostContextRequirement {
  dependencyId: `expression-home:${string}`;
  root: "props" | "event" | "env" | "route";
  path: readonly string[];
  expectedSchema?: HostValueSchema;
  sourceRefs: readonly ContentRef[];
  mandatory: true;
}

export interface RequiredCapabilitySliceDocument {
  schemaVersion: "cogo.required-capability-slice.v1";
  manifestHash: ContentHash;
  sourceInventoryRows: readonly RequiredCapabilitySourceInventoryRow[];
  semanticIdentities: readonly string[];
  archetypes: readonly string[];
  actions: readonly string[];
  actionConsumers: readonly RequiredActionConsumer[];
  aliases: readonly { alias: string; canonicalAction: string }[];
  parameterChannels: readonly RequiredParameterChannel[];
  resultChannels: readonly RequiredResultChannel[];
  expressionHomes: readonly string[];
  hostContextRequirements: readonly RequiredHostContextRequirement[];
  hostCapabilities: readonly RequiredHostCapability[];
  datasourceContracts: readonly string[];
  authContracts: readonly string[];
  stateTransitions: readonly string[];
  terminalOutcomes: readonly string[];
  dependencies: readonly RequiredCapabilityDependency[];
  sliceHash: ContentHash;
}

declare const requiredCapabilitySlice: unique symbol;

export type RequiredCapabilitySlice = RequiredCapabilitySliceDocument & {
  readonly [requiredCapabilitySlice]: true;
};

export type RequiredCapabilitySliceBody = Omit<
  RequiredCapabilitySliceDocument,
  "sliceHash" | "hostContextRequirements"
> & { hostContextRequirements?: readonly RequiredHostContextRequirement[] };

const HASH = /^sha256:[a-f0-9]{64}$/;
const PARAMETER_CHANNEL = /^parameter-channel:([^:]+):([^:]+)$/;
const RESULT_CHANNEL = /^result-channel:([^:]+):sha256:[a-f0-9]{64}$/;
const HOST_CHANNEL = /^host-channel:([^:]+):sha256:[a-f0-9]{64}$/;
const HOST_CAPABILITY = /^host\.[A-Za-z0-9][A-Za-z0-9._-]*$/;
const INVENTORY_KINDS = new Set<AcceptanceInventoryKind>([
  "role", "route", "surface", "journey", "branch", "event", "permission",
  "policy", "action", "state", "datasource", "error-path", "recovery-path",
  "terminal-outcome",
]);
const DEPENDENCY_ID: Record<RequiredCapabilityDependency["kind"], RegExp> = {
  "semantic-identity": /^semantic-identity:[^:]+$/,
  archetype: /^archetype:[^:]+$/,
  action: /^action:[^:]+$/,
  "expression-home": /^expression-home:[^:]+$/,
  "parameter-channel": PARAMETER_CHANNEL,
  "result-channel": RESULT_CHANNEL,
  host: /^host:[^:]+$/,
  datasource: /^datasource:[^:]+$/,
  "auth-contract": /^auth-contract:[^:]+$/,
  "state-transition": /^state-transition:[^:]+$/,
  "terminal-outcome": /^terminal-outcome:[^:]+$/,
};

function fail(code: string, label: string): never {
  throw new Error(`REQUIRED_CAPABILITY_SLICE_${code}:${label}`);
}

function assertPlainJson(
  value: unknown,
  path = "$",
  ancestors = new Set<object>(),
  allowUndefinedProperties = false,
): void {
  if (value === null || typeof value === "string" || typeof value === "boolean") return;
  if (typeof value === "number") {
    if (!Number.isFinite(value)) fail("NON_JSON", path);
    return;
  }
  if (typeof value !== "object" || ancestors.has(value)) fail("NON_JSON", path);
  const array = Array.isArray(value);
  if (
    Object.getPrototypeOf(value) !== (array ? Array.prototype : Object.prototype) ||
    Object.getOwnPropertySymbols(value).length > 0
  ) fail("NON_JSON", path);
  const descriptors = Object.getOwnPropertyDescriptors(value);
  if (array) {
    const actual = Object.keys(descriptors).filter((key) => key !== "length");
    const expected = Array.from({ length: value.length }, (_, index) => String(index));
    if (actual.length !== expected.length || actual.some((key, index) => key !== expected[index])) {
      fail("NON_JSON", path);
    }
  }
  const next = new Set(ancestors).add(value);
  for (const [key, descriptor] of Object.entries(descriptors)) {
    if (array && key === "length") continue;
    if (!descriptor.enumerable || !("value" in descriptor) || descriptor.get || descriptor.set) {
      fail("NON_JSON", `${path}.${key}`);
    }
    if (descriptor.value === undefined && allowUndefinedProperties && !array) continue;
    assertPlainJson(descriptor.value, `${path}.${key}`, next, allowUndefinedProperties);
  }
}

function assertExactKeys(
  value: unknown,
  required: readonly string[],
  optional: readonly string[],
  label: string,
): asserts value is Record<string, unknown> {
  if (!value || typeof value !== "object" || Array.isArray(value)) fail("INVALID", label);
  const allowed = new Set([...required, ...optional]);
  const keys = Object.keys(value);
  if (
    keys.some((key) => !allowed.has(key)) ||
    required.some((key) => !Object.prototype.hasOwnProperty.call(value, key))
  ) {
    fail("UNKNOWN_OR_MISSING_FIELD", label);
  }
}

function assertArray(value: unknown, label: string): asserts value is readonly unknown[] {
  if (!Array.isArray(value) || Object.getPrototypeOf(value) !== Array.prototype) fail("INVALID_ARRAY", label);
}

function assertText(value: unknown, label: string): asserts value is string {
  if (typeof value !== "string" || value.length === 0) fail("INVALID_TEXT", label);
}

function assertHash(value: unknown, label: string): asserts value is ContentHash {
  if (typeof value !== "string" || !HASH.test(value)) fail("INVALID_HASH", label);
}

function assertUnique<T>(values: readonly T[], identity: (value: T) => string, label: string): void {
  const seen = new Set<string>();
  for (const value of values) {
    const key = identity(value);
    if (seen.has(key)) fail("DUPLICATE", label);
    seen.add(key);
  }
}

function assertTextArray(value: unknown, label: string): asserts value is readonly string[] {
  assertArray(value, label);
  for (const entry of value) assertText(entry, label);
  assertUnique(value as readonly string[], (entry) => entry, label);
}

function assertContentRef(value: unknown, label: string): asserts value is ContentRef {
  assertExactKeys(value, ["ref", "contentHash"], [], label);
  assertText(value.ref, label);
  assertHash(value.contentHash, label);
}

function assertContentRefs(value: unknown, label: string): asserts value is readonly ContentRef[] {
  assertArray(value, label);
  if (value.length === 0) fail("EMPTY", label);
  for (const entry of value) assertContentRef(entry, label);
  assertUnique(value as readonly ContentRef[], (entry) => entry.ref, label);
}

function assertInventoryRows(
  value: unknown,
  label: string,
  requireOne = false,
): asserts value is readonly RequiredCapabilitySourceInventoryRow[] {
  assertArray(value, label);
  if (requireOne && value.length === 0) fail("EMPTY", label);
  for (const row of value) {
    assertExactKeys(row, ["kind", "id"], [], label);
    if (!INVENTORY_KINDS.has(row.kind as AcceptanceInventoryKind)) fail("INVALID_INVENTORY_KIND", label);
    assertText(row.id, label);
  }
  assertUnique(
    value as readonly RequiredCapabilitySourceInventoryRow[],
    (row) => `${row.kind}\u0000${row.id}`,
    label,
  );
}

function assertCondition(value: unknown, label: string): void {
  assertExactKeys(value, ["parameterPath"], ["present", "equals"], label);
  assertText(value.parameterPath, label);
  const hasPresent = value.present !== undefined;
  const hasEquals = value.equals !== undefined;
  if (hasPresent === hasEquals) fail("CONDITION_XOR", label);
  if (hasPresent && typeof value.present !== "boolean") fail("INVALID_CONDITION", label);
  if (hasEquals) {
    assertArray(value.equals, label);
    if (value.equals.length === 0) fail("EMPTY", label);
    for (const entry of value.equals) {
      if (
        entry !== null &&
        typeof entry !== "string" &&
        typeof entry !== "number" &&
        typeof entry !== "boolean"
      ) fail("INVALID_CONDITION", label);
    }
    assertUnique(value.equals, (entry) => stableCanonicalStringify(entry), label);
  }
}

function assertEnvelope(value: unknown, label: string): void {
  if (!value || typeof value !== "object" || Array.isArray(value)) fail("INVALID_ENVELOPE", label);
  const kind = (value as { kind?: unknown }).kind;
  if (kind === "unknown") {
    assertExactKeys(value, ["kind"], [], label);
    return;
  }
  if (kind === "scalar") {
    assertExactKeys(value, ["kind", "type"], [], label);
    if (!["string", "number", "boolean", "null"].includes(String(value.type))) fail("INVALID_ENVELOPE", label);
    return;
  }
  if (kind === "array") {
    assertExactKeys(value, ["kind", "items"], [], label);
    assertEnvelope(value.items, `${label}.items`);
    return;
  }
  if (kind !== "object") fail("INVALID_ENVELOPE", label);
  assertExactKeys(value, ["kind", "properties", "required"], [], label);
  if (!value.properties || typeof value.properties !== "object" || Array.isArray(value.properties)) fail("INVALID_ENVELOPE", label);
  for (const [key, nested] of Object.entries(value.properties)) {
    assertText(key, label);
    assertEnvelope(nested, `${label}.${key}`);
  }
  assertTextArray(value.required, `${label}.required`);
  if (value.required.some((key) => !Object.prototype.hasOwnProperty.call(value.properties, key))) {
    fail("ENVELOPE_REQUIRED_MISSING", label);
  }
}

function assertMandatory(value: unknown, label: string): void {
  if (value !== true) fail("MANDATORY_FALSE", label);
}

function canonicalObjectKey(value: unknown): string {
  return stableCanonicalStringify(value);
}

function uniqueSorted<T extends string>(values: Iterable<T>): T[] {
  return [...new Set(values)].sort(compareCanonicalText);
}

function uniqueSortedObjects<T>(values: Iterable<T>): T[] {
  const byKey = new Map<string, T>();
  for (const value of values) byKey.set(canonicalObjectKey(value), value);
  return [...byKey.entries()]
    .sort(([left], [right]) => compareCanonicalText(left, right))
    .map(([, value]) => value);
}

function uniqueSortedContentRefs(values: Iterable<ContentRef>): ContentRef[] {
  const byKey = new Map<string, ContentRef>();
  for (const value of values) {
    byKey.set(`${value.ref}\u0000${value.contentHash}`, {
      ref: value.ref,
      contentHash: value.contentHash,
    });
  }
  return [...byKey.values()].sort(
    (left, right) =>
      compareCanonicalText(left.ref, right.ref) ||
      compareCanonicalText(left.contentHash, right.contentHash),
  );
}

function canonicalCondition(
  value: ProjectedCapabilityCondition | undefined,
): ProjectedCapabilityCondition | undefined {
  if (!value) return undefined;
  return {
    parameterPath: value.parameterPath,
    ...(value.present !== undefined ? { present: value.present } : {}),
    ...(value.equals
      ? {
          equals: [...value.equals].sort((left, right) =>
            compareCanonicalText(JSON.stringify(left), JSON.stringify(right)),
          ),
        }
      : {}),
  };
}

function canonicalEnvelope(value: ConsumerValueSchema | undefined): ConsumerValueSchema | undefined {
  if (!value) return undefined;
  if (value.kind === "array") return { kind: "array", items: canonicalEnvelope(value.items)! };
  if (value.kind === "object") {
    return {
      kind: "object",
      properties: Object.fromEntries(
        Object.entries(value.properties)
          .sort(([left], [right]) => compareCanonicalText(left, right))
          .map(([key, nested]) => [key, canonicalEnvelope(nested)!]),
      ),
      required: uniqueSorted(value.required),
    };
  }
  return { ...value };
}

export function canonicalizeRequiredCapabilitySliceBody(
  value: RequiredCapabilitySliceBody,
): RequiredCapabilitySliceBody {
  const omitUndefined = <T>(entry: T): T =>
    JSON.parse(stableCanonicalStringify(entry)) as T;
  return omitUndefined({
    schemaVersion: value.schemaVersion,
    manifestHash: value.manifestHash,
    sourceInventoryRows: uniqueSortedObjects(value.sourceInventoryRows.map((row) => ({ ...row }))),
    semanticIdentities: uniqueSorted(value.semanticIdentities),
    archetypes: uniqueSorted(value.archetypes),
    actions: uniqueSorted(value.actions),
    actionConsumers: uniqueSortedObjects(value.actionConsumers.map((entry) => ({
      ...entry,
      aliases: uniqueSorted(entry.aliases),
      requiredHostCapabilityIds: uniqueSorted(entry.requiredHostCapabilityIds),
      sourceRefs: uniqueSortedContentRefs(entry.sourceRefs),
    }))),
    aliases: uniqueSortedObjects(value.aliases.map((entry) => ({ ...entry }))),
    parameterChannels: uniqueSortedObjects(value.parameterChannels.map((entry) => ({
      ...entry,
      aliases: uniqueSorted(entry.aliases),
      condition: canonicalCondition(entry.condition),
      sourceRefs: uniqueSortedContentRefs(entry.sourceRefs),
    }))),
    resultChannels: uniqueSortedObjects(value.resultChannels.map((entry) => ({
      ...entry,
      targetSuffixes: uniqueSorted(entry.targetSuffixes),
      condition: canonicalCondition(entry.condition),
      envelope: canonicalEnvelope(entry.envelope),
      defaultTargetFromParam: entry.defaultTargetFromParam ? { ...entry.defaultTargetFromParam } : undefined,
      sourceRefs: uniqueSortedContentRefs(entry.sourceRefs),
    }))),
    expressionHomes: uniqueSorted(value.expressionHomes),
    hostContextRequirements: uniqueSortedObjects((value.hostContextRequirements ?? []).map((entry) => ({
      ...entry,
      path: [...entry.path],
      expectedSchema: canonicalEnvelope(entry.expectedSchema),
      sourceRefs: uniqueSortedContentRefs(entry.sourceRefs),
    }))),
    hostCapabilities: uniqueSortedObjects(value.hostCapabilities.map((entry) => ({
      ...entry,
      condition: canonicalCondition(entry.condition),
      sourceRefs: uniqueSortedContentRefs(entry.sourceRefs),
    }))),
    datasourceContracts: uniqueSorted(value.datasourceContracts),
    authContracts: uniqueSorted(value.authContracts),
    stateTransitions: uniqueSorted(value.stateTransitions),
    terminalOutcomes: uniqueSorted(value.terminalOutcomes),
    dependencies: value.dependencies.map((entry) => ({
      ...entry,
      sourceInventoryRows: uniqueSortedObjects(entry.sourceInventoryRows.map((row) => ({ ...row }))),
      sourceRefs: uniqueSortedContentRefs(entry.sourceRefs),
    })).sort((left, right) => compareCanonicalText(
      `${left.kind}\u0000${left.catalogId}`,
      `${right.kind}\u0000${right.catalogId}`,
    )),
  });
}

function assertSliceShape(value: RequiredCapabilitySliceDocument): void {
  assertExactKeys(value, [
    "schemaVersion", "manifestHash", "sourceInventoryRows", "semanticIdentities",
    "archetypes", "actions", "actionConsumers", "aliases", "parameterChannels",
    "resultChannels", "expressionHomes", "hostContextRequirements", "hostCapabilities", "datasourceContracts",
    "authContracts", "stateTransitions", "terminalOutcomes", "dependencies", "sliceHash",
  ], [], "DOCUMENT");
  if (value.schemaVersion !== "cogo.required-capability-slice.v1") fail("SCHEMA_VERSION", "DOCUMENT");
  assertHash(value.manifestHash, "manifestHash");
  assertHash(value.sliceHash, "sliceHash");
  assertInventoryRows(value.sourceInventoryRows, "sourceInventoryRows");
  for (const [label, entries] of [
    ["semanticIdentities", value.semanticIdentities], ["archetypes", value.archetypes],
    ["actions", value.actions], ["expressionHomes", value.expressionHomes],
    ["datasourceContracts", value.datasourceContracts], ["authContracts", value.authContracts],
    ["stateTransitions", value.stateTransitions], ["terminalOutcomes", value.terminalOutcomes],
  ] as const) assertTextArray(entries, label);

  assertArray(value.hostContextRequirements, "hostContextRequirements");
  for (const row of value.hostContextRequirements) {
    assertExactKeys(row, ["dependencyId", "root", "path", "sourceRefs", "mandatory"], ["expectedSchema"], "hostContextRequirement");
    if (!/^expression-home:(props|event|env|route)\.[^:]+$/.test(row.dependencyId)) fail("INVALID_HOST_CONTEXT_DEPENDENCY", row.dependencyId);
    if (!["props", "event", "env", "route"].includes(row.root)) fail("INVALID_HOST_CONTEXT_ROOT", row.dependencyId);
    const segments = row.dependencyId.slice("expression-home:".length).split(".");
    if (segments[0] !== row.root || stableCanonicalStringify(segments.slice(1)) !== stableCanonicalStringify(row.path)) fail("HOST_CONTEXT_PATH_DRIFT", row.dependencyId);
    assertTextArray(row.path, `${row.dependencyId}.path`);
    if (row.expectedSchema !== undefined) assertEnvelope(row.expectedSchema, `${row.dependencyId}.expectedSchema`);
    assertContentRefs(row.sourceRefs, `${row.dependencyId}.sourceRefs`);
    assertMandatory(row.mandatory, row.dependencyId);
  }
  assertUnique(value.hostContextRequirements, (row) => row.dependencyId, "hostContextRequirements");

  assertArray(value.actionConsumers, "actionConsumers");
  for (const row of value.actionConsumers) {
    assertExactKeys(row, ["requestedAction", "canonicalAction", "aliases", "requiredHostCapabilityIds", "missingConsumerPolicy", "sourceRefs", "mandatory"], [], "actionConsumer");
    assertText(row.requestedAction, "requestedAction");
    assertText(row.canonicalAction, "canonicalAction");
    assertTextArray(row.aliases, "aliases");
    assertTextArray(row.requiredHostCapabilityIds, "requiredHostCapabilityIds");
    const invalidHostCapabilityId = row.requiredHostCapabilityIds.find(
      (id) => !HOST_CAPABILITY.test(id),
    );
    if (invalidHostCapabilityId) fail("INVALID_HOST_CAPABILITY", invalidHostCapabilityId);
    if (!["reject-before-side-effects", "block-host"].includes(row.missingConsumerPolicy)) fail("INVALID_POLICY", row.requestedAction);
    assertContentRefs(row.sourceRefs, "actionConsumer.sourceRefs");
    assertMandatory(row.mandatory, "actionConsumer");
  }
  assertUnique(value.actionConsumers, (row) => `${row.requestedAction}\u0000${row.canonicalAction}`, "actionConsumers");

  assertArray(value.aliases, "aliases");
  for (const row of value.aliases) {
    assertExactKeys(row, ["alias", "canonicalAction"], [], "alias");
    assertText(row.alias, "alias");
    assertText(row.canonicalAction, "canonicalAction");
  }
  assertUnique(value.aliases, (row) => row.alias, "aliases");

  assertArray(value.parameterChannels, "parameterChannels");
  for (const row of value.parameterChannels) {
    assertExactKeys(row, ["channelId", "requestedAction", "canonicalAction", "parameterPath", "aliases", "role", "sourceRefs", "runtimeRequired", "mandatory"], ["condition"], "parameterChannel");
    const match = PARAMETER_CHANNEL.exec(row.channelId);
    if (!match || match[1] !== row.canonicalAction || match[2] !== row.parameterPath) fail("CHANNEL_ID_DRIFT", row.channelId);
    assertText(row.requestedAction, "requestedAction");
    assertTextArray(row.aliases, "aliases");
    if (!["input", "selector", "payload", "target", "guard"].includes(row.role)) fail("INVALID_ROLE", row.channelId);
    if (row.condition) assertCondition(row.condition, `${row.channelId}.condition`);
    assertContentRefs(row.sourceRefs, `${row.channelId}.sourceRefs`);
    if (typeof row.runtimeRequired !== "boolean") fail("INVALID_RUNTIME_REQUIRED", row.channelId);
    assertMandatory(row.mandatory, row.channelId);
  }
  assertUnique(value.parameterChannels, (row) => `${row.channelId}\u0000${row.requestedAction}`, "parameterChannels");

  assertArray(value.resultChannels, "resultChannels");
  for (const row of value.resultChannels) {
    assertExactKeys(row, ["channelId", "requestedAction", "canonicalAction", "kind", "targetSuffixes", "sourceRefs", "mandatory"], ["target", "hostCapabilityId", "operation", "targetParam", "defaultTarget", "defaultTargetFromParam", "capture", "selectorParam", "payloadParam", "envelope", "condition"], "resultChannel");
    const match = RESULT_CHANNEL.exec(row.channelId);
    if (!match || match[1] !== row.canonicalAction) fail("CHANNEL_ID_DRIFT", row.channelId);
    assertText(row.requestedAction, "requestedAction");
    if (!["state-target", "return-capture", "host-effect", "host-registry"].includes(row.kind)) fail("INVALID_RESULT_KIND", row.channelId);
    for (const optionalText of [row.target, row.operation, row.targetParam, row.defaultTarget, row.selectorParam, row.payloadParam]) {
      if (optionalText !== undefined) assertText(optionalText, row.channelId);
    }
    if (row.hostCapabilityId !== undefined && !HOST_CAPABILITY.test(row.hostCapabilityId)) fail("INVALID_HOST_CAPABILITY", row.channelId);
    if (row.capture !== undefined && row.capture !== "step-name") fail("INVALID_CAPTURE", row.channelId);
    if (row.defaultTargetFromParam) {
      assertExactKeys(row.defaultTargetFromParam, ["prefix", "param"], ["fallback"], "defaultTargetFromParam");
      assertText(row.defaultTargetFromParam.prefix, row.channelId);
      assertText(row.defaultTargetFromParam.param, row.channelId);
      if (row.defaultTargetFromParam.fallback !== undefined) assertText(row.defaultTargetFromParam.fallback, row.channelId);
    }
    assertTextArray(row.targetSuffixes, "targetSuffixes");
    if (row.envelope) assertEnvelope(row.envelope, `${row.channelId}.envelope`);
    if (row.condition) assertCondition(row.condition, `${row.channelId}.condition`);
    assertContentRefs(row.sourceRefs, `${row.channelId}.sourceRefs`);
    assertMandatory(row.mandatory, row.channelId);
  }
  assertUnique(value.resultChannels, (row) => `${row.channelId}\u0000${row.requestedAction}`, "resultChannels");

  assertArray(value.hostCapabilities, "hostCapabilities");
  for (const row of value.hostCapabilities) {
    assertExactKeys(row, ["channelId", "requestedAction", "canonicalAction", "capabilityId", "operation", "sourceRefs", "mandatory"], ["condition"], "hostCapability");
    const match = HOST_CHANNEL.exec(row.channelId);
    if (!match || match[1] !== row.canonicalAction) fail("CHANNEL_ID_DRIFT", row.channelId);
    assertText(row.requestedAction, "requestedAction");
    if (!HOST_CAPABILITY.test(row.capabilityId)) fail("INVALID_HOST_CAPABILITY", row.channelId);
    assertText(row.operation, "operation");
    if (row.condition) assertCondition(row.condition, `${row.channelId}.condition`);
    assertContentRefs(row.sourceRefs, `${row.channelId}.sourceRefs`);
    assertMandatory(row.mandatory, row.channelId);
  }
  assertUnique(value.hostCapabilities, (row) => row.channelId, "hostCapabilities");

  assertArray(value.dependencies, "dependencies");
  for (const row of value.dependencies) {
    assertExactKeys(row, ["catalogId", "kind", "sourceInventoryRows", "sourceRefs", "mandatory"], [], "dependency");
    if (!Object.prototype.hasOwnProperty.call(DEPENDENCY_ID, row.kind)) {
      fail("INVALID_DEPENDENCY_KIND", String(row.kind));
    }
    assertText(row.catalogId, "catalogId");
    if (!DEPENDENCY_ID[row.kind].test(row.catalogId)) fail("INVALID_CATALOG_ID", row.catalogId);
    assertInventoryRows(row.sourceInventoryRows, "dependency.sourceInventoryRows", true);
    assertContentRefs(row.sourceRefs, "dependency.sourceRefs");
    assertMandatory(row.mandatory, row.catalogId);
  }
  assertUnique(value.dependencies, (row) => `${row.kind}\u0000${row.catalogId}`, "dependencies");
}

export function hashRequiredCapabilitySliceBody(
  value: RequiredCapabilitySliceBody,
): ContentHash {
  return hashCanonicalContent(canonicalizeRequiredCapabilitySliceBody(value));
}

export function createRequiredCapabilitySlice(
  value: RequiredCapabilitySliceBody,
): RequiredCapabilitySlice {
  assertPlainJson(value, "$", new Set(), true);
  assertExactKeys(value, [
    "schemaVersion", "manifestHash", "sourceInventoryRows", "semanticIdentities",
    "archetypes", "actions", "actionConsumers", "aliases", "parameterChannels",
    "resultChannels", "expressionHomes", "hostCapabilities", "datasourceContracts",
    "authContracts", "stateTransitions", "terminalOutcomes", "dependencies",
  ], ["hostContextRequirements"], "DOCUMENT");
  assertSliceShape({
    ...value,
    hostContextRequirements: value.hostContextRequirements ?? [],
    sliceHash: `sha256:${"0".repeat(64)}`,
  });
  const canonical = canonicalizeRequiredCapabilitySliceBody(value);
  const document = {
    ...canonical,
    sliceHash: hashCanonicalContent(canonical),
  } as RequiredCapabilitySlice;
  assertRequiredCapabilitySliceIntrinsicIntegrity(document);
  return document;
}

export function assertRequiredCapabilitySliceIntrinsicIntegrity(
  value: RequiredCapabilitySliceDocument,
): asserts value is RequiredCapabilitySlice {
  assertPlainJson(value);
  assertSliceShape(value);
  const { sliceHash, ...body } = value;
  if (hashRequiredCapabilitySliceBody(body) !== sliceHash) fail("HASH_MISMATCH", "sliceHash");
}

export function requiredCapabilitySliceContentRef(
  value: RequiredCapabilitySlice,
): ContentRef {
  assertRequiredCapabilitySliceIntrinsicIntegrity(value);
  return {
    ref: `cogo-content:required-capability-slice:${value.sliceHash}`,
    contentHash: value.sliceHash,
  };
}
